import {
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, Validators, FormsModule } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import {
    Payment,
    PaymentControllerService,
    AccessPlanControllerService,
    AccessPlan,
    Installment
} from '../../../core/generated';
import { EnumDescriptionPipe } from '../../../shared/pipes/enum-description.pipe';
import { PaymentDialogComponent } from './payment-dialog/payment-dialog.component';
import { PaymentDetailsDialogComponent } from '../../../shared/dialogs/payment-details-dialog/payment-details-dialog.component';

@Component({
    selector: 'app-admin-payment',
    standalone: true,
    imports: [MaterialModule, FormsModule, EnumDescriptionPipe],
    templateUrl: './admin-payment.component.html',
    styleUrl: './admin-payment.component.scss'
})
export class AdminPaymentComponent implements OnInit {
    private _snackBar = inject(MatSnackBar);
    private cd = inject(ChangeDetectorRef);
    private fb = inject(FormBuilder);
    private dialog = inject(MatDialog);
    private paymentsService = inject(PaymentControllerService);
    private accessPlansService = inject(AccessPlanControllerService);

    @ViewChild(MatTable) table!: MatTable<Payment>;

    dataSource = new MatTableDataSource<Payment>([]);
    payments: Payment[] = [];
    accessPlans: AccessPlan[] = [];
    selectedPayment: Payment | null = null;
    selectedInstallments: Installment[] = [];

    displayedColumns: string[] = [
        'id',
        'accessPlanId',
        'totalAmount',
        'method',
        'status',
        'actions',
    ];

    displayedInstallmentColumns: string[] = [
        'installmentNumber',
        'amount',
        'dueDate',
        'paidAt',
        'status',
        'actions',
    ];

    paymentStatus: Payment.StatusEnum[] = [
        'PENDING_PAYMENT',
        'PARTIALLY_PAID',
        'PAID',
        'FAILED',
    ];

    paymentMethods: Payment.MethodEnum[] = [
        'CREDIT_CARD',
        'PIX',
        'BOLETO',
        'BANK_TRANSFER',
    ];

    // Filtros
    filterCpfCnpj: string = '';
    filterStatus: Payment.StatusEnum | '' = '';
    showOnlyOverdue: boolean = false;

    // Form para criar pagamento
    createPaymentForm = this.fb.group({
        accessPlanId: [null as number | null, Validators.required],
        numberOfInstallments: [1, [Validators.required, Validators.min(1), Validators.max(12)]],
        method: ['CREDIT_CARD' as Payment.MethodEnum, Validators.required],
    });

    async ngOnInit() {
        await this.loadPayments();
        await this.loadAccessPlans();
    }

    // ========================================
    // MÉTODOS DE LISTAGEM
    // ========================================

    async loadPayments() {
        try {
            const cpfcnpj = this.filterCpfCnpj ? this.filterCpfCnpj.replace(/\D/g, '') : undefined;
            
            // Validação de CPF/CNPJ se fornecido
            if (cpfcnpj && cpfcnpj.length !== 11 && cpfcnpj.length !== 14) {
                this._snackBar.open('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', 'Fechar', {
                    duration: 3000,
                });
                return;
            }

            let response: any;

            // Se precisar buscar pagamentos em atraso
            if (this.showOnlyOverdue) {
                const paymentsArray = await firstValueFrom(
                    this.paymentsService.findPaymentsWithOverdueInstallments()
                );
                this.payments = paymentsArray || [];
            }
            // Usar o método de busca com filtros
            else {
                response = await firstValueFrom(
                    this.paymentsService.searchPayments(
                        cpfcnpj,
                        this.filterStatus || undefined,
                        0,
                        100,
                        'id'
                    )
                );

                if (response && response.content) {
                    this.payments = response.content;
                } else {
                    this.payments = [];
                }
            }

            this.dataSource.data = [...this.payments];
            this.cd.markForCheck();
        } catch (error) {
            this._snackBar.open('Erro ao carregar pagamentos', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro ao carregar pagamentos:', error);
            this.payments = [];
            this.dataSource.data = [];
        }
    }

    async loadPaymentById(id: number) {
        try {
            this.selectedPayment = await firstValueFrom(
                this.paymentsService.findById11(id)
            );

            // Carregar parcelas do pagamento
            if (this.selectedPayment?.id) {
                await this.loadInstallmentsByPaymentId(this.selectedPayment.id);
            }
        } catch (error) {
            this._snackBar.open('Erro ao carregar pagamento', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro:', error);
        }
    }

    openPaymentDetailsDialog(payment: Payment) {
        this.dialog.open(PaymentDetailsDialogComponent, {
            width: '900px',
            maxWidth: '95vw',
            data: { payment }
        });
    }

    async loadInstallmentsByPaymentId(paymentId: number) {
        try {
            this.selectedInstallments = await firstValueFrom(
                this.paymentsService.findInstallmentsByPayment(paymentId)
            );
        } catch (error) {
            this._snackBar.open('Erro ao carregar parcelas', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro:', error);
        }
    }

    // ========================================
    // MÉTODOS DE CRIAÇÃO DE PAGAMENTO
    // ========================================

    async createPaymentWithInstallments() {
        if (this.createPaymentForm.invalid) {
            this._snackBar.open('Por favor, preencha todos os campos obrigatórios', 'Fechar', {
                duration: 3000,
            });
            return;
        }

        try {
            // Buscar o plano para calcular o valor total
            const plan = this.accessPlans.find(p => p.id === this.createPaymentForm.value.accessPlanId);
            
            if (!plan) {
                this._snackBar.open('Plano não encontrado', 'Fechar', {
                    duration: 3000,
                });
                return;
            }

            const payment: Payment = {
                accessPlanId: this.createPaymentForm.value.accessPlanId!,
                totalAmount: plan.finalTotal || 0,
                numberOfInstallments: this.createPaymentForm.value.numberOfInstallments!,
                method: this.createPaymentForm.value.method!,
                status: 'PENDING_PAYMENT',
                planCpfcnpj: plan.cpfcnpj,
            };

            await firstValueFrom(
                this.paymentsService.save10(payment)
            );

            this._snackBar.open('Pagamento criado com sucesso!', 'Fechar', {
                duration: 3000,
            });

            this.resetCreatePaymentForm();
            await this.loadPayments();
        } catch (error) {
            this._snackBar.open('Erro ao criar pagamento', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro ao criar pagamento:', error);
        }
    }

    openCreatePaymentDialog() {
        const dialogRef = this.dialog.open(PaymentDialogComponent, {
            width: '500px',
            data: {}
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this.savePayment(result);
            }
        });
    }

    async savePayment(paymentData: any) {
        try {
            const payment: Payment = {
                accessPlanId: paymentData.accessPlanId,
                totalAmount: paymentData.totalAmount,
                numberOfInstallments: paymentData.numberOfInstallments || 1,
                method: paymentData.method,
                status: paymentData.status,
                planCpfcnpj: paymentData.planCpfcnpj,
            };

            if (paymentData.id) {
                // Atualizar pagamento existente
                await firstValueFrom(
                    this.paymentsService.update10(paymentData.id, payment)
                );
                this._snackBar.open('Pagamento atualizado com sucesso!', 'Fechar', {
                    duration: 3000,
                });
            } else {
                // Criar novo pagamento
                await firstValueFrom(
                    this.paymentsService.save10(payment)
                );
                this._snackBar.open('Pagamento criado com sucesso!', 'Fechar', {
                    duration: 3000,
                });
            }

            await this.loadPayments();
        } catch (error) {
            this._snackBar.open('Erro ao salvar pagamento', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro ao salvar pagamento:', error);
        }
    }

    // ========================================
    // MÉTODOS DE EDIÇÃO E EXCLUSÃO
    // ========================================

    editPayment(payment: Payment) {
        const dialogRef = this.dialog.open(PaymentDialogComponent, {
            width: '500px',
            data: { payment }
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this.savePayment(result);
            }
        });
    }

    async deletePayment(id: number) {
        if (!confirm('Tem certeza que deseja excluir este pagamento?')) {
            return;
        }

        try {
            await firstValueFrom(this.paymentsService.deleteById10(id));
            this._snackBar.open('Pagamento excluído com sucesso!', 'Fechar', {
                duration: 3000,
            });
            await this.loadPayments();
        } catch (error) {
            this._snackBar.open('Erro ao excluir pagamento', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro ao excluir pagamento:', error);
        }
    }

    // ========================================
    // MÉTODOS DE GERENCIAMENTO DE PARCELAS
    // ========================================

    async payInstallment(installmentId: number) {
        if (!confirm('Confirmar o pagamento desta parcela?')) {
            return;
        }

        try {
            await firstValueFrom(
                this.paymentsService.processInstallmentPayment(installmentId)
            );

            this._snackBar.open('Parcela paga com sucesso!', 'Fechar', {
                duration: 3000,
            });

            if (this.selectedPayment?.id) {
                await this.loadPaymentById(this.selectedPayment.id);
            }
            await this.loadPayments();
        } catch (error) {
            this._snackBar.open('Erro ao processar pagamento da parcela', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro:', error);
        }
    }

    async cancelInstallment(installmentId: number) {
        if (!confirm('Tem certeza que deseja cancelar esta parcela?')) {
            return;
        }

        try {
            await firstValueFrom(
                this.paymentsService.cancelInstallment(installmentId)
            );

            this._snackBar.open('Parcela cancelada com sucesso!', 'Fechar', {
                duration: 3000,
            });

            if (this.selectedPayment?.id) {
                await this.loadPaymentById(this.selectedPayment.id);
            }
            await this.loadPayments();
        } catch (error) {
            this._snackBar.open('Erro ao cancelar parcela', 'Fechar', {
                duration: 5000,
            });
            console.error('Erro:', error);
        }
    }

    // ========================================
    // MÉTODOS AUXILIARES
    // ========================================

    async loadAccessPlans() {
        try {
            const response = await firstValueFrom(
                this.accessPlansService.findAll11(0, 1000, 'id')
            );

            if (response && response.content && Array.isArray(response.content)) {
                this.accessPlans = response.content.filter((plan: AccessPlan) => plan.active);
            }
        } catch (error) {
            console.error('Erro ao carregar planos:', error);
        }
    }

    getPlanName(accessPlanId?: number): string {
        if (!accessPlanId) return 'N/A';
        const plan = this.accessPlans.find(p => p.id === accessPlanId);
        return plan ? `Plano #${accessPlanId} - ${this.formatCpfCnpj(plan.cpfcnpj)}` : `Plano #${accessPlanId}`;
    }

    clearFilters() {
        this.filterCpfCnpj = '';
        this.filterStatus = '';
        this.showOnlyOverdue = false;
        this.loadPayments();
    }

    applyFilters() {
        this.loadPayments();
    }

    resetCreatePaymentForm() {
        this.createPaymentForm.reset({
            accessPlanId: null,
            numberOfInstallments: 1,
            method: 'CREDIT_CARD',
        });
    }

    closePaymentDetails() {
        this.selectedPayment = null;
        this.selectedInstallments = [];
    }

    formatCpfCnpj(cpfCnpj?: string): string {
        if (!cpfCnpj) return 'N/A';

        const numbers = cpfCnpj.replace(/\D/g, '');

        if (numbers.length === 11) {
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }

        if (numbers.length === 14) {
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }

        return cpfCnpj;
    }

    formatDate(date?: string): string {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('pt-BR');
    }

    formatDateTime(date?: string): string {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('pt-BR');
    }

    formatPrice(price?: number): string {
        if (price === undefined || price === null) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(price);
    }

    getPaymentStatusColor(status?: Payment.StatusEnum): string {
        const colorMap: { [key: string]: string } = {
            'PENDING_PAYMENT': 'warn',
            'PARTIALLY_PAID': 'accent',
            'PAID': 'primary',
            'FAILED': 'warn'
        };
        return status ? colorMap[status] || '' : '';
    }

    getInstallmentStatusColor(status?: Installment.StatusEnum): string {
        const colorMap: { [key: string]: string } = {
            'PENDING_PAYMENT': 'warn',
            'PAID': 'primary',
            'LATE': 'warn',
            'CANCELLED_PAYMENT': '',
        };
        return status ? colorMap[status] || '' : '';
    }

    canPayInstallment(installment: Installment): boolean {
        return installment.status === 'PENDING_PAYMENT' || installment.status === 'LATE';
    }

    canCancelInstallment(installment: Installment): boolean {
        return installment.status === 'PENDING_PAYMENT';
    }

    getPaidInstallmentsCount(payment: Payment): number {
        return payment.installments?.filter(i => i.status === 'PAID').length || 0;
    }

    getTotalPaid(payment: Payment): number {
        return payment.installments
            ?.filter(i => i.status === 'PAID')
            .reduce((sum, i) => sum + (i.amount || 0), 0) || 0;
    }
}
